import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarDays, MapPin, PackageCheck, Undo2 } from 'lucide-react';
import { Badge, Button, Skeleton, cn, type BadgeTone } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { SupplyItem } from '../model/supply-items.js';
import { ItemCheckbox } from './ItemRow.js';

/**
 * SATRLAR — TELEFON VA PLANSHET KO'RINISHI.
 *
 * Nega `ResponsiveTable` ning umumiy kartochkasi EMAS: jadval qatoriga
 * beriladigan `doc-row` sinfi (`styles.css`, qatlamsiz, qat'iy 3.5rem)
 * `rowClassName` orqali kartochkaning `<li>` siga ham tushardi. Qatlamsiz
 * qoida Tailwind utilitalaridan ustun, ya'ni kartochka 56px ga qotib
 * qolar, ichidagi nom, maydonlar va 44px li tugma esa pastdagi
 * kartochkalar ustiga to'kilardi.
 *
 * Bu yerda kartochka balandligi FAQAT mazmundan keladi: hech qanday
 * `height`, `absolute` yoki virtualizatsiya yo'q. Ro'yxat baribir
 * sahifalangan (`PAGE_SIZE` = 50), shuning uchun bir vaqtda 50 tadan
 * ortiq kartochka chizilmaydi va virtualizatsiyaga ehtiyoj yo'q.
 */

export interface SupplyItemCardsProps {
  /** FAQAT joriy sahifadagi satrlar. */
  rows: SupplyItem[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onPlace: (item: SupplyItem) => void;
  onUndo: (lineId: string) => void;
  inBasket: ReadonlySet<string>;
  onShowLocation?: (item: SupplyItem) => void;
  canPlace: boolean;
  canSeeMoney: boolean;
  caption: string;
}

/** Kirish animatsiyasi faqat shuncha kartochkada — qolgani darhol turadi. */
const STAGGERED = 8;

const doneQty = (item: SupplyItem): number =>
  Math.round((item.balanceQty - item.remaining) * 1000) / 1000;

/** O'ng yuqoridagi holat nishoni — rang ham, matn ham bir narsani aytadi. */
const statusBadge = (
  item: SupplyItem,
): { key: string; tone: BadgeTone; label: string; title?: string } => {
  if (item.status === 'placed')
    return { key: 'placed', tone: 'success', label: t('supply.lineDone') };
  if (item.status === 'empty') {
    return {
      key: 'empty',
      tone: 'neutral',
      label: t('supply.lineEmpty'),
      title: t('supply.lineEmptyHint'),
    };
  }
  const done = doneQty(item);
  return done > 0
    ? {
        key: 'partial',
        tone: 'info',
        label: t('supply.qtyPlacedOf', { done, total: item.balanceQty }),
      }
    : {
        key: 'pending',
        tone: 'warn',
        label: t('supply.qtyLeft', { qty: item.remaining }),
      };
};

const Field = ({
  label,
  children,
  className,
  inline = false,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  /** Yorliq va qiymat bitta qatorda, ikki chetda. */
  inline?: boolean;
}) => (
  <div
    className={cn(
      'flex min-w-0',
      inline ? 'flex-row items-center justify-between gap-3' : 'flex-col gap-0.5',
      className,
    )}
  >
    <dt className="text-xs text-text-faint">{label}</dt>
    <dd className="min-w-0 text-sm break-words text-text">{children}</dd>
  </div>
);

interface ItemCardProps extends Omit<
  SupplyItemCardsProps,
  'rows' | 'selected' | 'inBasket' | 'caption'
> {
  item: SupplyItem;
  index: number;
  checked: boolean;
  inBasket: boolean;
  reduceMotion: boolean;
}

const ItemCard = ({
  item,
  index,
  checked,
  inBasket,
  onToggle,
  onPlace,
  onUndo,
  onShowLocation,
  canPlace,
  canSeeMoney,
  reduceMotion,
}: ItemCardProps) => {
  const placed = item.status === 'placed';
  const badge = statusBadge(item);
  const done = doneQty(item);
  const percent =
    item.balanceQty <= 0 ? 0 : Math.min(100, Math.round((done / item.balanceQty) * 100));
  const canLocate = onShowLocation !== undefined && done > 0;
  const animateIn = !reduceMotion && index < STAGGERED;

  const actions: ReactNode[] = [];
  if (canPlace && item.status === 'pending') {
    actions.push(
      <Button
        key="place"
        size="lg"
        variant="secondary"
        onClick={() => onPlace(item)}
        className="min-w-0 flex-1 active:scale-[0.98] motion-reduce:active:scale-100"
      >
        <PackageCheck size={16} aria-hidden />
        {t('supply.place')}
      </Button>,
    );
  }
  if (canLocate) {
    actions.push(
      <Button
        key="locate"
        size="lg"
        variant={actions.length === 0 ? 'secondary' : 'ghost'}
        onClick={() => onShowLocation(item)}
        aria-label={actions.length === 0 ? undefined : t('stock.locationOpen')}
        title={t('stock.locationOpen')}
        className={cn(
          'active:scale-[0.98] motion-reduce:active:scale-100',
          actions.length === 0 ? 'min-w-0 flex-1' : 'shrink-0 px-3',
        )}
      >
        <MapPin size={16} aria-hidden />
        {actions.length === 0 ? t('stock.locationOpen') : null}
      </Button>,
    );
  }
  if (inBasket) {
    actions.push(
      <Button
        key="undo"
        size="lg"
        variant="ghost"
        aria-label={t('supply.removeFromBasket')}
        title={t('supply.removeFromBasket')}
        onClick={() => onUndo(item.id)}
        className="shrink-0 px-3 active:scale-[0.98] motion-reduce:active:scale-100"
      >
        <Undo2 size={16} aria-hidden />
      </Button>,
    );
  }

  return (
    <motion.li
      data-row-key={item.id}
      initial={animateIn ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.22,
        ease: 'easeOut',
        delay: animateIn ? index * 0.035 : 0,
      }}
      className={cn(
        'flex h-full min-w-0 flex-col gap-3 rounded-2xl border p-4',
        'shadow-[var(--shadow-1)]',
        'transition-[background-color,border-color] duration-[var(--motion-slow)] ease-[var(--ease-standard)]',
        placed
          ? 'border-success/30 bg-[color-mix(in_oklab,var(--color-success-soft)_55%,var(--color-surface))]'
          : 'border-border bg-surface',
      )}
    >
      {/* TEPA QATOR: tanlash + tartib raqam chapda, holat o'ngda. */}
      <div className="flex min-h-7 items-center gap-2">
        {canPlace ? (
          /* 44px teginish maydoni — katakchaning o'zi 18px. */
          <span className="-my-2 -ml-2 flex size-11 shrink-0 items-center justify-center [&_input]:size-[1.125rem]">
            <ItemCheckbox item={item} checked={checked} onToggle={onToggle} />
          </span>
        ) : null}

        {/* Hujjatning O'Z satr raqami — qidiruvdan keyin ham o'zgarmaydi. */}
        <span
          aria-hidden
          className={cn(
            'flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full px-2',
            'bg-surface-2 text-xs font-semibold tabular-nums text-text-muted',
          )}
        >
          {item.lineNo}
        </span>

        <span className="ml-auto flex min-w-0 justify-end">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={badge.key}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={
                reduceMotion
                  ? { opacity: 0, transition: { duration: 0 } }
                  : { opacity: 0, scale: 0.92 }
              }
              transition={{ duration: 0.18 }}
              className="flex min-w-0"
            >
              <Badge tone={badge.tone} title={badge.title} className="max-w-full truncate">
                {badge.label}
              </Badge>
            </motion.span>
          </AnimatePresence>
        </span>
      </div>

      {/* NOM va KOD. */}
      <div className="flex min-w-0 flex-col gap-1">
        <h3
          title={item.name}
          className="line-clamp-2 text-base leading-snug font-semibold break-words text-text"
        >
          {item.name}
        </h3>
        <span className="font-mono text-xs tracking-tight text-text-muted">{item.code}</span>
      </div>

      {/* KIRIM SANASI — ajratilgan blok va maydonlarning BIRINCHISI:
          skladchi qaysi partiyani oldin joylashtirishni shunga qarab
          hal qiladi. */}
      <dl>
        <Field
          label={t('supply.colArrivedAt')}
          inline
          className="rounded-xl bg-surface-2 px-3 py-2"
        >
          {item.arrivedAt === null ? (
            <span className="text-text-faint">—</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums">
              <CalendarDays size={14} aria-hidden className="shrink-0 text-text-faint" />
              {item.arrivedAt}
            </span>
          )}
        </Field>
      </dl>

      {/* SONLAR — bir qarashda o'qiladigan uch katak. */}
      <dl className="grid grid-cols-3 gap-2 rounded-xl border border-border px-3 py-2.5">
        <Field label={t('supply.colQty')}>
          <span className="font-semibold tabular-nums">{item.balanceQty}</span>
        </Field>
        <Field label={t('supply.colUnit')}>
          <span title={item.unitRaw}>{unitText(item.unit)}</span>
        </Field>
        <Field label={t('supply.colToPlace')}>
          <span
            className={cn(
              'font-semibold tabular-nums',
              item.status === 'pending' ? 'text-accent' : 'text-text-muted',
            )}
          >
            {item.remaining}
          </span>
        </Field>
      </dl>

      {/* Ulush chizig'i — raqamni o'qimasdan ham ish qolganini ko'rsatadi.
          `aria-hidden`: ayni ma'lumot nishonda so'z bilan aytilgan. */}
      {item.status === 'empty' ? null : (
        <div aria-hidden className="h-1 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-[var(--motion-slow)] ease-[var(--ease-standard)]',
              percent === 100 ? 'bg-success' : 'bg-accent',
            )}
            style={{ width: `${String(percent)}%` }}
          />
        </div>
      )}

      {/* QOLGAN MAYDONLAR — jadvaldagi har ustun kartochkada ham bor. */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        <Field label={t('supply.colSupplier')} className="col-span-2">
          {item.supplier === null ? (
            <span className="text-text-muted">{t('supply.supplierUnknown')}</span>
          ) : (
            item.supplier
          )}
        </Field>
        {canSeeMoney ? (
          <>
            <Field label={t('supply.colPrice')}>
              <span className="tabular-nums">
                {item.price === null ? '—' : item.price.toLocaleString('ru-RU')}
              </span>
            </Field>
            <Field label={t('supply.colAccount')}>
              <span className="font-mono">{item.account ?? '—'}</span>
            </Field>
          </>
        ) : null}
      </dl>

      {/* AMALLAR — oqimda, kartochkaning eng pastida, to'liq enda. */}
      {actions.length === 0 ? null : (
        <div className="mt-auto flex items-center gap-2 border-t border-border pt-3">{actions}</div>
      )}
    </motion.li>
  );
};

export const SupplyItemCards = ({
  rows,
  selected,
  inBasket,
  caption,
  ...rest
}: SupplyItemCardsProps) => {
  const reduceMotion = useReducedMotion() === true;
  return (
    <ul aria-label={caption} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {rows.map((item, index) => (
        <ItemCard
          key={item.id}
          item={item}
          index={index}
          checked={selected.has(item.id)}
          inBasket={inBasket.has(item.id)}
          reduceMotion={reduceMotion}
          {...rest}
        />
      ))}
    </ul>
  );
};

/** Yuklanish holati — kartochkalar shaklida, sakrash bo'lmasin. */
export const SupplyItemCardsSkeleton = ({ count = 4 }: { count?: number }) => (
  <ul aria-hidden className="grid grid-cols-1 gap-3 md:grid-cols-2">
    {Array.from({ length: count }, (_, i) => (
      <li
        key={i}
        className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4 shadow-[var(--shadow-1)]"
      >
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-10" />
          <Skeleton className="h-6 w-24" />
        </div>
        <Skeleton className="h-5 w-11/12" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-11 w-full" />
      </li>
    ))}
  </ul>
);
